import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FilterDropdownProps {
  /** Prefix shown before the chosen value, e.g. "PARTNER:". */
  label: string
  value: string
  options: string[]
  onChange: (option: string) => void
  /** Hover text for the trigger — used to carry upstream's caveat about a dimension. */
  title?: string
  /** Tighter chip, for a page tuned to fit the viewport. */
  compact?: boolean
}

/**
 * The scope chips on a system page: a label, the chosen value, and a panel of
 * options drawn on the app's own palette.
 *
 * A native `<select>` was the obvious build and the wrong one — the browser
 * draws its option list with the OS widget, so the open menu arrived as a pale
 * list in the middle of a dark screen and ignored every token in the design
 * system. This is a button plus a `role="listbox"` panel, which costs us the
 * keyboard behaviour a native select gave for free — so that is implemented
 * here rather than dropped: Enter/Space/arrows to open, arrows and Home/End to
 * move, type-ahead to jump, Enter to choose, Escape to dismiss, and focus
 * returned to the trigger on the way out.
 */
export function FilterDropdown({
  label,
  value,
  options,
  onChange,
  title,
  compact,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false)
  /** Option the keyboard is on; -1 when nothing is highlighted. */
  const [activeIndex, setActiveIndex] = useState(-1)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLUListElement>(null)
  /** Letters typed in the last second, for jump-to-option. */
  const typed = useRef({ text: '', at: 0 })
  const baseId = useId().replace(/:/g, '')
  const labelId = baseId + '-label'
  const listId = baseId + '-list'

  const close = (restoreFocus = true) => {
    setOpen(false)
    setActiveIndex(-1)
    if (restoreFocus) trigger.current?.focus()
  }

  const choose = (index: number) => {
    const option = options[index]
    if (option) onChange(option)
    close()
  }

  /** Open with the current value highlighted, so arrows move from there. */
  const openFrom = (index = Math.max(options.indexOf(value), 0)) => {
    setOpen(true)
    setActiveIndex(index)
  }

  // Dismiss on an outside press. Pointerdown rather than click, so dragging out
  // of the panel and releasing doesn't leave it open.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (trigger.current?.contains(target) || panel.current?.contains(target)) return
      setOpen(false)
      setActiveIndex(-1)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  // Keep the keyboard's option in view when the panel scrolls.
  useEffect(() => {
    if (!open || activeIndex < 0) return
    panel.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Closed: these keys open the panel rather than doing nothing.
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        openFrom()
      }
      return
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        close()
        return
      case 'Tab':
        // Let focus leave, but don't leave a menu hanging over the page.
        close(false)
        return
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % options.length)
        return
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + options.length) % options.length)
        return
      case 'Home':
        e.preventDefault()
        setActiveIndex(0)
        return
      case 'End':
        e.preventDefault()
        setActiveIndex(options.length - 1)
        return
      case 'Enter':
      case ' ':
        e.preventDefault()
        choose(activeIndex)
        return
    }

    // Type-ahead: consecutive letters build a prefix, and a pause starts over.
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = Date.now()
      typed.current.text = now - typed.current.at > 1000 ? e.key : typed.current.text + e.key
      typed.current.at = now
      const prefix = typed.current.text.toLowerCase()
      const found = options.findIndex((o) => o.toLowerCase().startsWith(prefix))
      if (found >= 0) setActiveIndex(found)
    }
  }

  return (
    <div className="relative">
      <button
        ref={trigger}
        type="button"
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? close(false) : openFrom())}
        onKeyDown={onKeyDown}
        className={cn(
          'border-line bg-surface hover:border-line-bright flex items-center gap-2 rounded-lg border',
          'focus-visible:border-primary/60 focus-visible:ring-primary/25 transition-colors',
          'outline-none focus-visible:ring-2',
          // An open chip reads as pressed, so it belongs with its panel.
          open && 'border-primary/60 bg-surface-2',
          compact ? 'px-3 py-1.5' : 'px-3 py-2',
        )}
      >
        <span
          id={labelId}
          className="text-fg-subtle font-mono text-[11px] font-semibold tracking-[0.08em] uppercase"
        >
          {label}:
        </span>
        <span className="text-fg font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          {value}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            'text-fg-subtle size-3.5 transition-transform duration-150',
            open && 'text-fg-muted rotate-180',
          )}
        />
      </button>

      {open && (
        <ul
          ref={panel}
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          aria-activedescendant={activeIndex >= 0 ? baseId + '-opt-' + activeIndex : undefined}
          className={cn(
            'border-line-bright bg-surface-2 animate-pop-in absolute top-[calc(100%+6px)] left-0 z-30',
            'max-h-64 min-w-full overflow-y-auto rounded-lg border p-1 shadow-xl shadow-black/40',
          )}
        >
          {options.map((option, i) => {
            const selected = option === value
            return (
              <li
                key={option}
                id={baseId + '-opt-' + i}
                role="option"
                aria-selected={selected}
                // Hovering moves the keyboard's position too, so the mouse and
                // the arrow keys can never disagree about what is highlighted.
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => choose(i)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2',
                  'font-mono text-[11px] font-semibold tracking-[0.08em] whitespace-nowrap uppercase',
                  i === activeIndex ? 'bg-surface-3 text-fg' : 'text-fg-muted',
                  selected && 'text-primary-bright',
                )}
              >
                <Check
                  aria-hidden
                  className={cn('size-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
                />
                {option}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
